import { useState, useLayoutEffect, useEffect, useMemo } from 'react'

import {
  VideoObjectPaginatedList,
  VideosVideoIdDeleteRequest,
  VideosGetRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'
import { pickBy } from 'lodash-es'

import { videoObjectMutationKeys } from 'hooks/mutations/content'
import { videoObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects'
import { videoObjectQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId } = videoObjectsOverviewRoute.useParams()
  const search = videoObjectsOverviewRoute.useSearch()
  const { query, timeRangeStart, timeRangeEnd } = search

  const sortProperties = useMemo(
    () => pickBy(search, (_, key) => key.startsWith('orderBy')) as PickStartsWith<VideosGetRequest, 'orderBy'>,
    [search],
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const videosQuery = useInfiniteQuery({
    ...videoObjectQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId,
      query,
      timeRangeStart,
      timeRangeEnd,
      ...sortProperties,
    }),
    enabled: workspaceQuery.isSuccess,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(videosQuery.isSuccess)

  const pendingVideoObjects = useMutationState({
    filters: { mutationKey: videoObjectMutationKeys.delete(), status: 'pending' },
    select: (mutation) => mutation.state.variables as VideosVideoIdDeleteRequest,
  })

  const videoBatchQuery = useQuery({
    ...videoObjectQueryKeys.batchGet(
      {},
      () => virtualItems?.map((virtualItem) => videosQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.NORMAL,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (videosQuery.isSuccess && videoBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<VideoObjectPaginatedList>>(
        { queryKey: videoObjectQueryKeys.lists() },
        (old) => {
          if (old) {
            return {
              ...old,
              pages:
                old.pages.map((page) => ({
                  ...page,
                  items: mergeBatchIntoSource(
                    page.items,
                    videoBatchQuery.data.items,
                    new Set(videoBatchQuery.data.notFoundIds),
                  ),
                })) ?? [],
            }
          }
          return old
        },
      )
    }
  }, [
    videoBatchQuery.data?.items,
    videoBatchQuery.data?.notFoundIds,
    videoBatchQuery.isSuccess,
    videosQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingVideoObjects.map((task) => task.videoId)])])
  }, [pendingVideoObjects, setLoadingRows])

  useEffect(() => {
    if (videosQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, videosQuery.isSuccess])

  return {
    workspaceQuery,
    videosQuery,
    setBatchGetEnabled,
  }
}
